/**
 * DropboxScopeCard — can this environment read Dropbox FILE BYTES?
 *
 * Built 2026-06-21. The scope denial behind the unrecoverable avatar was
 * measured in PREVIEW, and preview has its own Dropbox credentials — so it
 * could not be claimed as a production fact. This card is how production gets
 * measured: one button, no console, no terminal.
 *
 * Read-only. Nothing is written, moved or deleted. `permitted_not_found` counts
 * as a pass: the call was authorised, the probe path simply is not there.
 */
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertOctagon, AlertTriangle, Loader2, HardDriveDownload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const PROBE_LABELS = {
  files_search_v2: 'files/search_v2 (metadata read)',
  files_download: 'files/download (READ BYTES)',
  files_get_temporary_link: 'files/get_temporary_link (READ BYTES)',
  users_get_current_account: 'users/get_current_account (account info)',
};

const verdictTone = (v) => {
  if (v === 'green_content_readable') return { cls: 'border-emerald-300 bg-emerald-50 text-emerald-900', Icon: CheckCircle2 };
  if (v === 'amber_no_client') return { cls: 'border-amber-300 bg-amber-50 text-amber-900', Icon: AlertTriangle };
  return { cls: 'border-red-300 bg-red-50 text-red-900', Icon: AlertOctagon };
};

const VERDICT_TEXT = {
  green_content_readable: 'GREEN — file bytes are readable. The upload fallback can recover files.',
  red_metadata_only: 'RED — metadata reads work but file BYTES are denied. Storage looks connected while nothing can be recovered.',
  red_no_reads: 'RED — Dropbox reads are failing for both metadata and content.',
  amber_no_client: 'AMBER — no Dropbox client in this environment (credentials missing).',
};

const probeTone = (value) => {
  if (value === 'works' || value === 'permitted_not_found') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (value === 'denied') return 'bg-red-100 text-red-800 border-red-200';
  if (value === 'not_run') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-amber-100 text-amber-900 border-amber-200';
};

export default function DropboxScopeCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (forceFresh) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/diagnostics/dropbox-scope${forceFresh ? '?force_fresh=true' : ''}`,
        { headers: authHeaders() },
      );
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dropbox scope check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const tone = data ? verdictTone(data.verdict) : null;

  return (
    <Card className="mb-4" data-testid="dropbox-scope-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <HardDriveDownload className="w-5 h-5 text-slate-500" />
          Dropbox file-content scope
        </CardTitle>
        <CardDescription>
          Measures THIS environment: can the app read Dropbox file bytes, or only metadata?
          Read-only — nothing is written, moved or deleted. Run it on production to confirm
          whether the preview denial applies there too.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(false)} disabled={loading} data-testid="dropbox-scope-run-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Run check
          </Button>
          <Button
            variant="outline"
            onClick={() => run(true)}
            disabled={loading}
            data-testid="dropbox-scope-force-btn"
          >
            Run fresh (bypass 5-min cache)
          </Button>
        </div>

        {data && (
          <div className="space-y-3" data-testid="dropbox-scope-result">
            <div className={`p-3 rounded-lg border flex items-start gap-2 ${tone.cls}`} data-testid="dropbox-scope-verdict">
              <tone.Icon className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm font-medium">{VERDICT_TEXT[data.verdict] || data.verdict}</div>
            </div>

            <div className="space-y-1">
              {Object.entries(PROBE_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 text-sm border rounded-md px-3 py-2 bg-white"
                  data-testid={`dropbox-scope-probe-${key}`}
                >
                  <span className="text-slate-700">{label}</span>
                  <Badge variant="outline" className={`font-mono text-[11px] ${probeTone(data.probes?.[key])}`}>
                    {String(data.probes?.[key] ?? 'n/a')}
                  </Badge>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="dropbox-scope-meta">
              <div>is_storage_configured(): <span className="font-mono">{String(data.is_storage_configured)}</span></div>
              <div>can_read_content: <span className="font-mono">{String(data.can_read_content)}</span></div>
              <div>can_read_metadata: <span className="font-mono">{String(data.can_read_metadata)}</span></div>
              <div>Dropbox app key (last 4): <span className="font-mono">…{data.app_key_last4 || '????'}</span></div>
              <div className="sm:col-span-2 break-all">upload folder: <span className="font-mono">{data.upload_folder}</span></div>
              <div className="sm:col-span-2 break-all">probe path: <span className="font-mono">{data.probe_path}</span></div>
              <div>probed at: <span className="font-mono">{data.probed_at}</span></div>
              <div>cached: <span className="font-mono">{String(data.cached)}</span></div>
              <div className="sm:col-span-2">build: <span className="font-mono">{data.environment?.app_version} · {data.environment?.git_commit}</span></div>
            </div>

            {data.verdict === 'red_metadata_only' && (
              <div className="p-3 rounded-lg border border-red-200 bg-white text-sm space-y-1" data-testid="dropbox-scope-remedy">
                <div className="font-medium text-red-800">To fix, in the Dropbox App Console:</div>
                <ol className="list-decimal ml-5 space-y-1 text-slate-700">
                  <li>Open <span className="font-mono">dropbox.com/developers/apps</span> → this app (key ending …{data.app_key_last4}).</li>
                  <li>Permissions tab → tick <span className="font-mono">files.content.read</span> (keep <span className="font-mono">files.content.write</span> and <span className="font-mono">files.metadata.read</span>), then Submit.</li>
                  <li>Re-authorise: generate a NEW refresh token — existing tokens keep the OLD scope set and will stay denied.</li>
                  <li>Update <span className="font-mono">DROPBOX_REFRESH_TOKEN</span> and redeploy, then press “Run fresh” here.</li>
                </ol>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
